import { getFamilyPack, joinFamilyPack } from './familyService.js';
import { clearPendingInvitation, getPendingInvitationCode } from '../utils/pendingInvitation.js';
import type { FamilyPack } from '../types/family.js';

export type PendingInvitationResult = {
  accepted: boolean;
  authRefreshNeeded: boolean;
};

export type TryAcceptPendingInvitationOptions = {
  /** When bootstrap already fetched family pack, skip a duplicate GET. */
  existingFamilyPack?: FamilyPack | null;
};

/**
 * Accept a stored or URL `?invite=` code once. Clears the pending invite on
 * success, if the user already belongs to a family, or when acceptance fails
 * so stale codes are not retried on every app load.
 */
export async function tryAcceptPendingInvitation(
  options?: TryAcceptPendingInvitationOptions
): Promise<PendingInvitationResult> {
  const inviteCode = getPendingInvitationCode();
  if (!inviteCode) {
    return { accepted: false, authRefreshNeeded: false };
  }

  try {
    const existingFamily =
      options?.existingFamilyPack !== undefined
        ? options.existingFamilyPack
        : await getFamilyPack().catch(() => null);
    if (existingFamily?.id) {
      clearPendingInvitation();
      return { accepted: false, authRefreshNeeded: false };
    }

    await joinFamilyPack(inviteCode);
    clearPendingInvitation();
    return { accepted: true, authRefreshNeeded: true };
  } catch (error) {
    clearPendingInvitation();
    console.error('Family invitation could not be accepted', error);
    return { accepted: false, authRefreshNeeded: false };
  }
}
